"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useExperienciaProduto } from "@/components/ExperienciaProvider";
import { NavigationItem } from "@/components/NavigationItem";
import { ehRotaDriver, ITENS_DRIVER, itemDriverAtivo } from "@/lib/biblioteca-navegacao";
import {
  NAVEGACAO_FOLLOWZAP,
  ehAreaAnaliseGestorFollowZap,
  ehAreaGerencialFollowPro,
  itemSubmenuFollowZapAtivo,
  menuOcultaConfiguracoes,
  modulosFollowZapParaContexto,
  submenuFollowZapDoPathname,
  type FollowZapModuloId,
  type ItemSubmenuFollowZap,
} from "@/lib/followzap-navigation";
import {
  BarChart3,
  BriefcaseBusiness,
  CalendarDays,
  CalendarClock,
  ClipboardList,
  Columns3,
  ContactRound,
  FileText,
  Funnel,
  GitBranch,
  Heart,
  HardDrive,
  Trash2,
  UserRound,
  History,
  ImageIcon,
  Home,
  Inbox,
  List,
  MessageSquareText,
  Megaphone,
  Mic,
  PlayCircle,
  Package,
  Shield,
  ShoppingCart,
  Sparkles,
  Tags,
  UserPlus,
  Users,
  Webhook,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import type { ExperienciaProduto, RecursoProduto } from "@/lib/experiencia-produto";

const ITENS_USUARIO: Array<{ href: string; label: string; Icon: LucideIcon; recurso?: RecursoProduto; produtos?: readonly ExperienciaProduto["produto"][]; somenteFollowZap?: boolean; ocultarNoFollowZap?: boolean }> = [
  { href: "/", label: "Início", Icon: Home, recurso: "inicio" },
  { href: "/profissional/mensagens", label: "Fluxos", Icon: Sparkles, recurso: "biblioteca" },
  { href: "/drive", label: "Driver", Icon: Sparkles, produtos: ["FOLLOW_ZAP", "FOLLOW_PRO"] },
  { href: "/follow-up", label: "Follow-up", Icon: CalendarClock, recurso: "followUp" },
  { href: "/pipeline", label: "Pipeline", Icon: Columns3, recurso: "pipeline" },
  { href: "/acompanhamento", label: "Acompanhamentos", Icon: ClipboardList, recurso: "acompanhamento", ocultarNoFollowZap: true },
  { href: "/cadastro-novo", label: "Agendar e Cadastrar", Icon: UserPlus, recurso: "clientes", ocultarNoFollowZap: true },
  { href: "/etiquetas", label: "Etiquetas", Icon: Tags, recurso: "etiquetas", somenteFollowZap: true },
  { href: "/crm", label: "CRM", Icon: CalendarDays, recurso: "crm" },
  { href: "/cadastro-lista", label: "Agenda de Retorno", Icon: FileText, ocultarNoFollowZap: true },
  { href: "/carteira", label: "Carteira", Icon: BriefcaseBusiness, recurso: "carteira" },
  { href: "/produtos", label: "Produtos", Icon: Package, recurso: "vendas", produtos: ["FOLLOW_ZAP"] },
  { href: "/ferramenta", label: "Ferramenta", Icon: Wrench, recurso: "ferramenta" },
  { href: "/aprenda", label: "Aprenda", Icon: PlayCircle, recurso: "aprenda" },
  { href: "/captacao", label: "Captação", Icon: Inbox, recurso: "integracoes" },
  { href: "/integracoes", label: "Integrações", Icon: Wrench, recurso: "integracoes" },
  { href: "/leads", label: "Leads", Icon: ContactRound, recurso: "leads" },
  { href: "/analise", label: "Análise", Icon: BarChart3, recurso: "analise", produtos: ["FOLLOW_ZAP"] },
  { href: "/meta-ads", label: "Meta Ads", Icon: Megaphone, recurso: "anuncios" },
  { href: "/configuracoes", label: "Configurações", Icon: BarChart3, recurso: "configuracoes" },
];

const SUBMENU_DRIVER = ITENS_DRIVER.map((item) => ({ ...item, Icon: item.href === "/drive/pessoal" ? UserRound : item.href === "/drive/lixeira" ? Trash2 : HardDrive }));

const ITENS_FLUXOS: Array<{ href: string; label: string; Icon: LucideIcon }> = [
  { href: "/profissional/mensagens", label: "Mensagens", Icon: MessageSquareText },
  { href: "/profissional/audios", label: "Áudios", Icon: Mic },
  { href: "/profissional/midias", label: "Mídias", Icon: ImageIcon },
  { href: "/profissional/funis", label: "Funis", Icon: Funnel },
  { href: "/profissional/favoritos", label: "Favoritos", Icon: Heart },
  { href: "/profissional/etiquetas", label: "Etiquetas", Icon: Tags },
];

const ITENS_CAPTACAO: Array<{ href: string; label: string; Icon: LucideIcon }> = [
  { href: "/captacao/formulario-meta", label: "Formulário Meta", Icon: ContactRound },
  { href: "/captacao/followzap", label: "FollowZap", Icon: Webhook },
];

const ITENS_META_ADS: Array<{ href: string; label: string; Icon: LucideIcon }> = [
  { href: "/meta-ads/campanhas", label: "Campanhas", Icon: Megaphone },
  { href: "/meta-ads/objetivos", label: "Objetivos", Icon: BarChart3 },
  { href: "/meta-ads/contas-de-anuncio", label: "Contas de anúncio", Icon: Wrench },
];

const ITENS_ANALISE: Array<{ href: string; label: string; Icon: LucideIcon }> = [
  { href: "/analise/rendimento", label: "Vendas", Icon: ShoppingCart },
  { href: "/analise/leads", label: "Leads", Icon: Users },
  { href: "/analise/entrada-distribuicao", label: "Distribuição", Icon: GitBranch },
  { href: "/analise/atendimento", label: "Atendimento", Icon: MessageSquareText },
];

const ITENS_LEADS: Array<{ href: string; label: string; Icon: LucideIcon }> = [
  { href: "/leads/lista", label: "Lista", Icon: List },
  { href: "/leads/historico", label: "Histórico", Icon: History },
];

const ICONES_MODULOS_FOLLOWZAP: Record<FollowZapModuloId, LucideIcon> = {
  inicio: Home,
  fluxos: Sparkles,
  driver: Sparkles,
  "follow-up": CalendarClock,
  etiquetas: Tags,
  crm: CalendarDays,
  carteira: BriefcaseBusiness,
  produtos: Package,
  captacao: Inbox,
  integracoes: Wrench,
  leads: ContactRound,
  equipe: Users,
  analise: BarChart3,
  "meta-ads": Megaphone,
  configuracoes: BarChart3,
};

const ICONES_SUBMENUS_FOLLOWZAP: Record<string, LucideIcon> = {
  "/profissional/mensagens": MessageSquareText,
  "/profissional/audios": Mic,
  "/profissional/midias": ImageIcon,
  "/profissional/funis": Funnel,
  "/profissional/favoritos": Heart,
  "/profissional/etiquetas": Tags,
  "/drive/pessoal": UserRound,
  "/drive/meu-driver": HardDrive,
  "/drive/lixeira": Trash2,
  "/captacao/formulario-meta": ContactRound,
  "/captacao/followzap": Webhook,
  "/leads/lista": List,
  "/leads/historico": History,
  "/followpro/equipe/integrantes": Users,
  "/analise/rendimento": ShoppingCart,
  "/analise/leads": Users,
  "/analise/entrada-distribuicao": GitBranch,
  "/analise/atendimento": MessageSquareText,
  "/meta-ads/campanhas": Megaphone,
  "/meta-ads/objetivos": BarChart3,
  "/meta-ads/contas-de-anuncio": Wrench,
};

function adicionarIconesSubmenuFollowZap(itens: readonly ItemSubmenuFollowZap[]) {
  return itens.map((item) => ({ ...item, Icon: ICONES_SUBMENUS_FOLLOWZAP[item.href] }));
}

const DICAS = [
  "Quem acompanha rápido objeções e devolve resposta com clareza vende com mais confiança.",
  "Desempenho constante vale mais que picos. Um bom dia de vendas começa no primeiro retorno feito.",
  "Cliente sem retorno esfria. Contato no tempo certo aumenta chance de fechamento.",
  "Perguntas objetivas abrem venda: entenda a dor, confirme a urgência e conduza o próximo passo.",
  "Meta alta se bate em bloco pequeno: um bom contato agora aproxima o fechamento do dia.",
  "Venda boa nasce de escuta boa. Quem entende o cliente responde melhor.",
  "Retorno rápido mostra cuidado e reduz a chance do cliente esfriar.",
  "Uma conversa objetiva vale mais que várias mensagens sem direção.",
  "Todo contato bem feito aproxima o cliente da decisão.",
  "Consistência diária vence pressa sem método.",
  "Quem organiza a carteira ganha tempo para vender melhor.",
  "O melhor argumento é mostrar que você entendeu a necessidade.",
  "Cliente em dúvida precisa de segurança, não de pressão.",
  "Venda forte começa em um acompanhamento bem conduzido.",
  "Quando você responde no tempo certo, a confiança cresce.",
  "O próximo fechamento pode estar no retorno que você fizer agora.",
  "Meta do mês se constrói no cuidado com cada cliente do dia.",
  "Quem acompanha com ritmo evita perder oportunidade boa.",
  "Cliente lembrado no momento certo responde melhor.",
  "Atenção aos detalhes transforma conversa em resultado.",
  "Vender bem também é saber a hora certa de retomar o contato.",
  "Um bom dia de vendas pede foco no que está mais quente.",
  "Toda ligação com objetivo claro economiza tempo e aumenta resultado.",
  "Quem mantém o ritmo encontra mais oportunidades de fechamento.",
  "Clareza na fala transmite segurança para o cliente.",
  "Pequenas evoluções diárias criam grandes semanas de desempenho.",
  "Cliente bem atendido lembra de quem acompanhou com atenção.",
  "Agendar o retorno certo vale mais que insistir na hora errada.",
  "A carteira organizada mostra onde está a melhor chance do dia.",
  "Venda consultiva começa com pergunta certa e escuta ativa.",
  "O cliente percebe quando o acompanhamento é feito com intenção.",
  "Cada retorno feito no prazo protege sua meta.",
  "Fechamento não depende de sorte. Depende de ritmo e acompanhamento.",
  "Quem revisa prioridades vende com mais previsibilidade.",
  "Disciplina comercial também é responder quando combinou.",
  "Toda resposta rápida reduz distância entre interesse e decisão.",
  "Contato atrasado custa mais do que parece. Antecipe o movimento.",
  "O melhor momento para retomar um cliente é antes dele esfriar.",
  "Foco no essencial: atender bem, registrar certo e seguir o próximo passo.",
  "Uma carteira viva pede ritmo, atenção e constância.",
];

function dicaDoDia() {
  const dia = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "numeric",
  }).format(new Date());
  return DICAS[(Number(dia) || 1) % DICAS.length];
}

export function UserDesktopSidebar({
  activeHref,
  sticky = false,
  compact = false,
  integrado = false,
  modoAvancado = true,
  mostrarFollowUp = false,
  papel,
}: {
  activeHref?: string;
  sticky?: boolean;
  compact?: boolean;
  integrado?: boolean;
  modoAvancado?: boolean;
  mostrarFollowUp?: boolean;
  papel?: string;
}) {
  const { dados, experiencia, carregado: experienciaCarregada, experienciaConfirmada } = useExperienciaProduto();
  const pathname = usePathname();
  const areaGerencialAnalise = ehAreaGerencialFollowPro(pathname, dados?.produto, dados?.papel);
  const areaAnaliseGestorFollowZap = ehAreaAnaliseGestorFollowZap(pathname, dados?.produto, dados?.papel);
  const followProUsuario = dados?.produto === "FOLLOW_PRO" && dados.papel === "USUARIO";
  const [dicaAtual, setDicaAtual] = useState(dicaDoDia);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setDicaAtual((atual) => {
        const indiceAtual = Math.max(0, DICAS.indexOf(atual));
        return DICAS[(indiceAtual + 1) % DICAS.length];
      });
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  const modulosFollowZap = experienciaCarregada && experienciaConfirmada
    ? modulosFollowZapParaContexto({
        produto: experiencia.produto,
        papel: dados?.papel ?? null,
        recursos: experiencia.recursos,
        modoAvancado,
        mostrarFollowUp,
        areaAnaliseGestorFollowZap,
        areaGerencialAnalise,
        modulosFollowProUsuario: dados?.modulosFollowProUsuario,
      })
    : null;
  const usandoCatalogoFollowZap = modulosFollowZap !== null;
  const itensVisiveis = experienciaCarregada && experienciaConfirmada
    ? modulosFollowZap?.map((item) => ({ ...item, Icon: ICONES_MODULOS_FOLLOWZAP[item.id] })) ?? ITENS_USUARIO.filter(({ recurso, href, produtos, somenteFollowZap, ocultarNoFollowZap }) =>
      (href !== "/pipeline" || (experienciaCarregada && experiencia.recursos.pipeline)) &&
      (!produtos || produtos.includes(experiencia.produto)) &&
      (!recurso || experiencia.recursos[recurso]) &&
      (href !== "/analise" || dados?.papel === "USUARIO" || dados?.papel === "GESTOR") &&
      (modoAvancado || !href.startsWith("/profissional")) &&
      (!ocultarNoFollowZap || experiencia.produto !== "FOLLOW_ZAP") &&
      (!somenteFollowZap || (experiencia.produto === "FOLLOW_ZAP" && papel === "USUARIO")) &&
      (mostrarFollowUp || href !== "/follow-up") &&
      (href !== "/configuracoes" || !menuOcultaConfiguracoes(experiencia.produto, dados?.papel))
    )
    : [];
  const submenuFollowZap = usandoCatalogoFollowZap
    ? submenuFollowZapDoPathname(pathname, "desktop", modulosFollowZap ?? NAVEGACAO_FOLLOWZAP.modulos)
    : null;
  const itensSubmenuFollowZap = submenuFollowZap ? adicionarIconesSubmenuFollowZap(submenuFollowZap.itens) : [];

  return (
    <div
      data-user-desktop-sidebar="true"
      className={`${sticky ? `sticky ${integrado ? "top-0" : "top-8"} ` : ""}flex w-full flex-col border border-[var(--fz-sidebar-border)] bg-[var(--fz-sidebar-bg)] lg:min-h-0 ${integrado ? "lg:h-dvh rounded-none border-0 shadow-none" : "lg:h-[calc(100dvh-4rem)] shadow-[var(--shadow-soft)]"} ${compact ? `${integrado ? "" : "rounded-[30px]"} px-3 py-5` : `${integrado ? "" : "rounded-[24px]"} px-4 py-5`}`}
    >
      <div>
        <div className={`flex ${integrado ? "" : "border-b border-[var(--fz-sidebar-divider)]"} ${compact ? "justify-center pb-5" : "items-center gap-2 pb-4"}`}>
          <div
            data-user-desktop-brand="true"
            className={`flex items-center justify-center font-bold text-white ${compact ? "h-14 w-14 rounded-[18px] text-lg" : "h-11 w-11 rounded-[14px] text-sm"}`}
          >
            {areaGerencialAnalise ? "FP" : experienciaCarregada && experienciaConfirmada ? experiencia.marca.sigla : null}
          </div>
          {!compact ? (
            <div className="min-w-0">
              {experienciaCarregada && experienciaConfirmada ? <p className="truncate text-[13px] font-semibold text-[var(--text-primary)]">{areaGerencialAnalise ? "FollowPro" : experiencia.marca.nome}</p> : null}
              <p className="text-[11px] text-[var(--text-soft)]">{areaGerencialAnalise || areaAnaliseGestorFollowZap ? "Gestão" : followProUsuario ? "Usuário" : "Consultor"}</p>
            </div>
          ) : null}
        </div>

        <nav className={compact ? "mt-7 space-y-2.5" : "mt-5 space-y-2"}>
          {itensVisiveis.map(({ href, label, Icon }) => {
            const fluxosAberto = href === "/profissional/mensagens" && (usandoCatalogoFollowZap ? submenuFollowZap?.moduloId === "fluxos" : pathname.startsWith("/profissional/"));
            const captacaoAberta = (href === "/captacao" || href === "/captacao/formulario-meta") && (usandoCatalogoFollowZap ? submenuFollowZap?.moduloId === "captacao" : pathname === "/captacao" || pathname.startsWith("/captacao/"));
            const metaAdsAberto = href === "/meta-ads" && (usandoCatalogoFollowZap ? submenuFollowZap?.moduloId === "meta-ads" : pathname === "/meta-ads" || pathname.startsWith("/meta-ads/"));
            const leadsAberto = (href === "/leads" || href === "/leads/lista" || href === "/leads/historico") && (usandoCatalogoFollowZap ? submenuFollowZap?.moduloId === "leads" : pathname === "/leads" || pathname.startsWith("/leads/"));
            const analiseAberta = href === "/analise" && (usandoCatalogoFollowZap ? submenuFollowZap?.moduloId === "analise" : pathname === "/analise" || pathname.startsWith("/analise/"));

            return (
              <div key={href}>
                <NavigationItem
                  href={href}
                  label={label}
                  Icon={Icon}
                  active={href === activeHref}
                  compact={compact}
                />
                {href === "/drive" && (usandoCatalogoFollowZap ? submenuFollowZap?.moduloId === "driver" : ehRotaDriver(pathname)) && !compact ? (
                  <div className="mt-1 ml-5 space-y-1 border-l border-[var(--fz-sidebar-divider)] pl-3">
                    {(usandoCatalogoFollowZap ? itensSubmenuFollowZap : SUBMENU_DRIVER).map((item) => {
                      const ativo = usandoCatalogoFollowZap ? itemSubmenuFollowZapAtivo(pathname, item) : itemDriverAtivo(pathname, item.href);
                      return (
                        <Link key={item.href} href={item.href} prefetch aria-current={ativo ? "page" : undefined}
                          className={`flex h-8 items-center gap-2 rounded-lg px-2 text-[11.5px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fz-sidebar-active-icon)] ${ativo ? "bg-[var(--fz-sidebar-active-bg)] font-semibold text-[var(--fz-sidebar-active-text)]" : "text-[var(--fz-sidebar-text)] hover:bg-[var(--fz-sidebar-hover-bg)]"}`}>
                          <item.Icon className={`h-3.5 w-3.5 ${ativo ? "text-[var(--fz-sidebar-active-icon)]" : "text-[var(--fz-sidebar-icon)]"}`} />
                          <span className="min-w-0 truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
                {fluxosAberto && !compact ? (
                  <div className="mt-1 ml-5 space-y-1 border-l border-[var(--fz-sidebar-divider)] pl-3">
                    {(usandoCatalogoFollowZap ? itensSubmenuFollowZap : ITENS_FLUXOS).map((item) => {
                      const ativo = usandoCatalogoFollowZap ? itemSubmenuFollowZapAtivo(pathname, item) : pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          prefetch
                          aria-current={ativo ? "page" : undefined}
                          className={`flex h-8 items-center gap-2 rounded-lg px-2 text-[11.5px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fz-sidebar-active-icon)] ${
                            ativo
                              ? "bg-[var(--fz-sidebar-active-bg)] font-semibold text-[var(--fz-sidebar-active-text)]"
                              : "text-[var(--fz-sidebar-text)] hover:bg-[var(--fz-sidebar-hover-bg)]"
                          }`}
                        >
                          <item.Icon className={`h-3.5 w-3.5 ${ativo ? "text-[var(--fz-sidebar-active-icon)]" : "text-[var(--fz-sidebar-icon)]"}`} />
                          <span className="min-w-0 truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
                {captacaoAberta && !compact ? (
                  <div className="mt-1 ml-5 space-y-1 border-l border-[var(--fz-sidebar-divider)] pl-3">
                    {(usandoCatalogoFollowZap ? itensSubmenuFollowZap : ITENS_CAPTACAO).map((item) => {
                      const ativo = usandoCatalogoFollowZap ? itemSubmenuFollowZapAtivo(pathname, item) : pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          prefetch
                          aria-current={ativo ? "page" : undefined}
                          className={`flex h-8 items-center gap-2 rounded-lg px-2 text-[11.5px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fz-sidebar-active-icon)] ${
                            ativo
                              ? "bg-[var(--fz-sidebar-active-bg)] font-semibold text-[var(--fz-sidebar-active-text)]"
                              : "text-[var(--fz-sidebar-text)] hover:bg-[var(--fz-sidebar-hover-bg)]"
                          }`}
                        >
                          <item.Icon className={`h-3.5 w-3.5 ${ativo ? "text-[var(--fz-sidebar-active-icon)]" : "text-[var(--fz-sidebar-icon)]"}`} />
                          <span className="min-w-0 truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
                {metaAdsAberto && !compact ? (
                  <div className="mt-1 ml-5 space-y-1 border-l border-[var(--fz-sidebar-divider)] pl-3">
                    {(usandoCatalogoFollowZap ? itensSubmenuFollowZap : ITENS_META_ADS).map((item) => {
                      const ativo = usandoCatalogoFollowZap ? itemSubmenuFollowZapAtivo(pathname, item) : pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          prefetch
                          aria-current={ativo ? "page" : undefined}
                          className={`flex h-8 items-center gap-2 rounded-lg px-2 text-[11.5px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fz-sidebar-active-icon)] ${
                            ativo
                              ? "bg-[var(--fz-sidebar-active-bg)] font-semibold text-[var(--fz-sidebar-active-text)]"
                              : "text-[var(--fz-sidebar-text)] hover:bg-[var(--fz-sidebar-hover-bg)]"
                          }`}
                        >
                          <item.Icon className={`h-3.5 w-3.5 ${ativo ? "text-[var(--fz-sidebar-active-icon)]" : "text-[var(--fz-sidebar-icon)]"}`} />
                          <span className="min-w-0 truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
                {leadsAberto && !compact ? (
                  <div className="mt-1 ml-5 space-y-1 border-l border-[var(--fz-sidebar-divider)] pl-3">
                    {(usandoCatalogoFollowZap ? itensSubmenuFollowZap : ITENS_LEADS).map((item) => {
                      const ativo = usandoCatalogoFollowZap ? itemSubmenuFollowZapAtivo(pathname, item) : pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          prefetch
                          aria-current={ativo ? "page" : undefined}
                          className={`flex h-8 items-center gap-2 rounded-lg px-2 text-[11.5px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fz-sidebar-active-icon)] ${
                            ativo
                              ? "bg-[var(--fz-sidebar-active-bg)] font-semibold text-[var(--fz-sidebar-active-text)]"
                              : "text-[var(--fz-sidebar-text)] hover:bg-[var(--fz-sidebar-hover-bg)]"
                          }`}
                        >
                          <item.Icon className={`h-3.5 w-3.5 ${ativo ? "text-[var(--fz-sidebar-active-icon)]" : "text-[var(--fz-sidebar-icon)]"}`} />
                          <span className="min-w-0 truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
                {analiseAberta && !compact ? (
                  <div className="mt-1 ml-5 space-y-1 border-l border-[var(--fz-sidebar-divider)] pl-3">
                    {(usandoCatalogoFollowZap ? itensSubmenuFollowZap : ITENS_ANALISE).map((item) => {
                      const ativo = usandoCatalogoFollowZap ? itemSubmenuFollowZapAtivo(pathname, item) : pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          prefetch
                          aria-current={ativo ? "page" : undefined}
                          className={`flex h-8 items-center gap-2 rounded-lg px-2 text-[11.5px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fz-sidebar-active-icon)] ${
                            ativo
                              ? "bg-[var(--fz-sidebar-active-bg)] font-semibold text-[var(--fz-sidebar-active-text)]"
                              : "text-[var(--fz-sidebar-text)] hover:bg-[var(--fz-sidebar-hover-bg)]"
                          }`}
                        >
                          <item.Icon className={`h-3.5 w-3.5 ${ativo ? "text-[var(--fz-sidebar-active-icon)]" : "text-[var(--fz-sidebar-icon)]"}`} />
                          <span className="min-w-0 truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
      </div>

      {!areaGerencialAnalise && !areaAnaliseGestorFollowZap && !followProUsuario ? <div className={`mt-auto border border-[var(--fz-sidebar-border)] bg-[var(--fz-sidebar-hover-bg)] text-center ${compact ? "rounded-[24px] px-2 py-4" : "rounded-[18px] px-3 py-4"}`}>
        <div
          data-user-desktop-tip-icon="true"
          className={`mx-auto flex items-center justify-center bg-[var(--dashboard-icon-chip)] text-[var(--fz-sidebar-shield-icon,#A7CA5A)] ${compact ? "h-10 w-10 rounded-[12px]" : "h-8 w-8 rounded-[10px]"}`}
        >
          <Shield className={compact ? "h-4.5 w-4.5" : "h-4 w-4"} />
        </div>
        {!compact ? (
          <>
            <p className="mt-2.5 text-[14px] font-semibold leading-tight text-[var(--fz-sidebar-active-text)]">Dica Bem Protege</p>
            <p className="mt-2 line-clamp-4 text-[11px] leading-5 text-[var(--fz-sidebar-text)]">{dicaAtual}</p>
          </>
        ) : null}
      </div> : null}
    </div>
  );
}
